import {
  Box,
  Button,
  DropdownItem,
  Icons,
  Modal,
  Table,
  Tabs,
  InputField,
  Typography,
  useToast,
  ColumnDatas,
  getColor,
  DropdownBody,
} from "fds";
import { useCallback, useEffect, useRef, useState } from "react";
import "./index.scss";
import { useTranslation } from "react-i18next";
import { nanoid } from "@reduxjs/toolkit";
import useAdminPositionRoleStore from "../../redux/dispatcher/useAdminPositionRoleStore";
import { CodeDto, PositionOrRoleDto } from "../../types/adminPositionRole";
import useCheckboxGroup from "fds/src/hooks/useCheckboxGroup";

const Position: React.FC = () => {
  const {
    positionRoleState,
    getPositionList,
    updatePosition,
    insertPosition,
    deletePosition,
    getRoleList,
    insertRole,
    updateRole,
    deleteRole,
  } = useAdminPositionRoleStore();

  /**** State 모음 ****/
  const [tab, setTab] = useState(0);
  const [height, setHeight] = useState(window.innerHeight - 260);
  const checkboxGroup = useCheckboxGroup<string>();
  const [positionList, setPositionList] = useState([] as PositionOrRoleDto[]);
  const [roleList, setRoleList] = useState([] as PositionOrRoleDto[]);
  const [showInsertPositionOrRoleModal, toggleInsertPositionOrRole] =
    useState(false);
  const [showUpdatePositionOrRoleModal, toggleUpdatePositionOrRole] =
    useState(false);
  const [newPositionOrRole, setNewPostionOrRole] = useState({
    name: "",
    code: "",
    order: -1,
  });
  const { t } = useTranslation();
  const toast = useToast();
  const tableRef = useRef<HTMLDivElement>(null);

  const [positionMenu, setPositionMenu] = useState<{
    x: number;
    y: number;
    target: PositionOrRoleDto;
  }>({
    x: 0,
    y: 0,
    target: {} as PositionOrRoleDto,
  });

  const [roleMenu, setRoleMenu] = useState<{
    x: number;
    y: number;
    target: PositionOrRoleDto;
  }>({
    x: 0,
    y: 0,
    target: {} as PositionOrRoleDto,
  });
  const inputRef = useRef<HTMLTableRowElement>(null);

  /**** UseEffect 모음 ****/
  useEffect(() => {
    setRoleList(positionRoleState.roleList);
    checkboxGroup.handleAllClick([]);
  }, [positionRoleState.roleList]);
  useEffect(() => {
    setPositionList(positionRoleState.positionList);
    checkboxGroup.handleAllClick([]);
  }, [positionRoleState.positionList]);

  useEffect(() => {
    getRoleList();
  }, []);

  useEffect(() => {
    checkboxGroup.handleAllClick([]);
    if (tab === 0) {
      getPositionList();
    } else if (tab === 1) {
      getRoleList();
    }
  }, [tab]);

  /*** Function 모음***/
  /** updatePosition: position의 상태를 업데이트 할 수 있다. */
  const updatePositionFunc = async (
    code: string,
    name: string,
    order: number,
    direction: number
  ) => {
    var newOrder = 0;
    switch (direction) {
      case 1:
        newOrder = order > 1 ? order - 1 : 1;
        updatePosition({
          code: positionList.filter((item) => item.order === newOrder)[0].code,
          name: positionList.filter((item) => item.order === newOrder)[0].name,
          order: order,
        });
        break;
      case 2:
        newOrder =
          order < positionList.length ? order + 1 : positionList.length;
        updatePosition({
          code: positionList.filter((item) => item.order === newOrder)[0].code,
          name: positionList.filter((item) => item.order === newOrder)[0].name,
          order: order,
        });
        break;
      case 3:
        newOrder = 1;
        positionList
          .filter((item) => item.order < order)
          .map((item1) => {
            updatePosition({
              code: item1.code,
              name: item1.name,
              order: item1.order + 1,
            });
          });
        break;
      case 4:
        newOrder = positionList.length;
        positionList
          .filter((item) => item.order > order)
          .map((item1) => {
            updatePosition({
              code: item1.code,
              name: item1.name,
              order: item1.order - 1,
            });
          });
        break;
      default:
        break;
    }

    await updatePosition({ code: code, name: name, order: newOrder });

    getPositionList();
  };

  /** deletePositionOrRole: role이나 position을 삭제하는 함수 */
  const deletePositionOrRole = async (code?: string) => {
    let result: any;
    let deleteLength = 0;
    let totalLength = tab === 0 ? positionList.length : roleList.length;
    const confirmDeletePositionOrRole = window.confirm("삭제하시겠습니까?");
    if (!confirmDeletePositionOrRole) return;
    var codes = [] as CodeDto[];
    if (code) {
      codes = [{ code: code }];
      result =
        tab === 0 ? await deletePosition(codes) : await deleteRole(codes);
      deleteLength = 1;
    } else if (tab === 0) {
      checkboxGroup.selected.map((item) => codes.push({ code: item }));
      result = await deletePosition(codes);
      deleteLength = checkboxGroup.selected.length;
    } else {
      checkboxGroup.selected.map((item) => codes.push({ code: item }));
      result = await deleteRole(codes);
      deleteLength = checkboxGroup.selected.length;
    }
    if (totalLength - result.payload.length !== deleteLength) {
      tab === 0
        ? toast.toastMsg(nanoid(), t("admin.positionRole.noDeleteJob"), "error")
        : toast.toastMsg(
            nanoid(),
            t("admin.positionRole.noDeleteRole"),
            "error"
          );
    } else {
      toast.toastMsg(nanoid(), t("admin.common.alert.delete"), "success");
    }
  };

  const insertPositionOrRole = async (name: string) => {
    let result: any =
      tab === 0 ? await insertPosition(name) : await insertRole(name);
    if (result.error) {
      switch (result.error.message) {
        case "EXIST_POSITION_NAME":
          toast.toastMsg(
            nanoid(),
            t("admin.positionRole.duplicateJob"),
            "error"
          );
          break;
        case "EXIST_ROLE_NAME":
          toast.toastMsg(
            nanoid(),
            t("admin.positionRole.duplicateRole"),
            "error"
          );
          break;
        default:
          break;
      }
    } else {
      toast.toastMsg(
        nanoid(),
        tab === 0 ? t("admin.common.alert.add") : t("admin.common.alert.add"),
        "success"
      );
    }
    setNewPostionOrRole({ name: "", code: "", order: -1 });
    toggleInsertPositionOrRole(false);
  };
  const updatePositionOrRole = async () => {
    let result: any =
      tab === 0
        ? await updatePosition({
            name: newPositionOrRole.name.trim(),
            code: newPositionOrRole.code,
            order: newPositionOrRole.order,
          })
        : await updateRole({
            name: newPositionOrRole.name.trim(),
            code: newPositionOrRole.code,
            order: newPositionOrRole.order,
          });
    if (result.error) {
      switch (result.error.name) {
        case "EXIST_POSITION_NAME":
          toast.toastMsg(
            nanoid(),
            t("admin.positionRole.duplicateJob"),
            "error"
          );
          break;
        case "EXIST_ROLE_NAME":
          toast.toastMsg(
            nanoid(),
            t("admin.positionRole.duplicateRole"),
            "error"
          );
          break;
        default:
          break;
      }
    } else {
      toast.toastMsg(
        nanoid(),
        tab === 0
          ? t("admin.common.alert.modify")
          : t("admin.common.alert.modify"),
        "success"
      );
    }
    setNewPostionOrRole({ name: "", code: "", order: -1 });
    toggleUpdatePositionOrRole(false);
  };
  /** updateRole: role의 상태를 업데이트 할 수 있다. */
  const updateRoleFunc = async (
    code: string,
    name: string,
    order: number,
    direction: number
  ) => {
    var newOrder = 0;
    switch (direction) {
      case 1:
        newOrder = order > 1 ? order - 1 : 1;
        updateRole({
          code: roleList.filter((item) => item.order === newOrder)[0].code,
          name: roleList.filter((item) => item.order === newOrder)[0].name,
          order: order,
        });
        break;
      case 2:
        newOrder = order < roleList.length ? order + 1 : roleList.length;
        updateRole({
          code: roleList.filter((item) => item.order === newOrder)[0].code,
          name: roleList.filter((item) => item.order === newOrder)[0].name,
          order: order,
        });
        break;
      case 3:
        newOrder = 1;
        roleList
          .filter((item) => item.order < order)
          .map((item1) => {
            updateRole({
              code: item1.code,
              name: item1.name,
              order: item1.order + 1,
            });
          });
        break;
      case 4:
        newOrder = roleList.length;
        roleList
          .filter((item) => item.order > order)
          .map((item1) => {
            updateRole({
              code: item1.code,
              name: item1.name,
              order: item1.order - 1,
            });
          });
        break;
      default:
        break;
    }

    await updateRole({ code: code, name: name, order: newOrder });
    getRoleList();
  };

  function transformPositionList(data: any[] | undefined) {
    const dataTransformed = [] as ColumnDatas[];
    data &&
      data?.map((item: any, index: number) => {
        const dataItemTransformed = {
          data: [
            {
              columnText: item.order,
            },
            {
              columnText: item.name,
            },
          ],
          id: item.code,
          checkBoxClick: () => {
            checkboxGroup.handleClick(item.code);
          },
          hover: (
            <>
              <Button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  updatePositionFunc(item.code, item.name, item.order, 1);
                }}
                btnStyle="icon"
                type="primary"
                size="sm"
                leadingIcon={
                  <Icons variant="line" label="chevron" direction="up" />
                }
              />
              <Button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  updatePositionFunc(item.code, item.name, item.order, 2);
                }}
                btnStyle="icon"
                type="primary"
                size="sm"
                leadingIcon={
                  <Icons variant="line" label="chevron" direction="down" />
                }
              />
              <Button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  updatePositionFunc(item.code, item.name, item.order, 3);
                }}
                btnStyle="icon"
                type="primary"
                size="sm"
                leadingIcon={
                  <Icons variant="line" label="doubleChevron" direction="up" />
                }
              />
              <Button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  updatePositionFunc(item.code, item.name, item.order, 4);
                }}
                btnStyle="icon"
                type="primary"
                size="sm"
                leadingIcon={
                  <Icons
                    variant="line"
                    label="doubleChevron"
                    direction="down"
                  />
                }
              />
              <Button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setPositionMenu({
                    x: e.pageX,
                    y: e.pageY,
                    target: item,
                  });
                }}
                btnStyle="icon"
                type="primary"
                size="sm"
                leadingIcon={<Icons variant="line" label="moreVertical" />}
              />
            </>
          ),
        };
        dataTransformed.push(dataItemTransformed);
      });
    return dataTransformed;
  }

  function transformRoleList(data: any[] | undefined) {
    const dataTransformed = [] as ColumnDatas[];
    data &&
      data?.map((item: any, index: number) => {
        const dataItemTransformed = {
          data: [
            {
              columnText: item.order,
            },
            {
              columnText: item.name,
            },
          ],
          id: item.code,
          checkBoxClick: () => {
            checkboxGroup.handleClick(item.code);
          },
          hover: (
            <>
              <Button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  updateRoleFunc(item.code, item.name, item.order, 1);
                }}
                btnStyle="icon"
                type="primary"
                size="sm"
                leadingIcon={
                  <Icons variant="line" label="chevron" direction="up" />
                }
              />
              <Button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  updateRoleFunc(item.code, item.name, item.order, 2);
                }}
                btnStyle="icon"
                type="primary"
                size="sm"
                leadingIcon={
                  <Icons variant="line" label="chevron" direction="down" />
                }
              />
              <Button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  updateRoleFunc(item.code, item.name, item.order, 3);
                }}
                btnStyle="icon"
                type="primary"
                size="sm"
                leadingIcon={
                  <Icons variant="line" label="doubleChevron" direction="up" />
                }
              />
              <Button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  updateRoleFunc(item.code, item.name, item.order, 4);
                }}
                btnStyle="icon"
                type="primary"
                size="sm"
                leadingIcon={
                  <Icons
                    variant="line"
                    label="doubleChevron"
                    direction="down"
                  />
                }
              />
              <Button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setRoleMenu({
                    x: e.pageX,
                    y: e.pageY,
                    target: item,
                  });
                }}
                btnStyle="icon"
                type="primary"
                size="sm"
                leadingIcon={<Icons variant="line" label="moreVertical" />}
              />
            </>
          ),
        };
        dataTransformed.push(dataItemTransformed);
      });
    return dataTransformed;
  }

  const renderPositionMenuButton = (target: any) => {
    return (
      <DropdownBody
        dropdownRef={inputRef}
        width={190}
        location={{ x: target.x, y: target.y }}
      >
        <DropdownItem
          label={t("admin.common.button.modify")}
          onClick={() => {
            setNewPostionOrRole({
              name: target.target.name,
              code: target.target.code,
              order: target.target.order,
            });
            toggleUpdatePositionOrRole(true);
            handleTurnOffSettng();
          }}
        />
        <DropdownItem
          label={t("admin.common.button.delete")}
          onClick={() => {
            deletePositionOrRole(target.target.code);
            handleTurnOffSettng();
          }}
        />
      </DropdownBody>
    );
  };

  const renderRoleMenuButton = (target: any) => {
    return (
      <DropdownBody
        dropdownRef={inputRef}
        width={190}
        location={{ x: target.x, y: target.y }}
      >
        <DropdownItem
          label={t("admin.common.button.modify")}
          onClick={() => {
            setNewPostionOrRole({
              name: target.target.name,
              code: target.target.code,
              order: target.target.order,
            });
            toggleUpdatePositionOrRole(true);
            handleTurnOffSettng();
          }}
        />
        <DropdownItem
          label={t("admin.common.button.delete")}
          onClick={() => {
            deletePositionOrRole(target.target.code);
            handleTurnOffSettng();
          }}
        />
      </DropdownBody>
    );
  };

  useEffect(() => {
    const handleResize = () => {
      if (tableRef.current) {
        const rect = tableRef.current.getBoundingClientRect();
        setHeight(window.innerHeight - rect.top - 72);
      }
    };

    const element = tableRef.current;

    const resizeObserver = new ResizeObserver(() => {
      if (element) {
        const rect = element.getBoundingClientRect();
        setHeight(window.innerHeight - rect.top - 72);
      }
    });

    if (element) {
      resizeObserver.observe(element);
    }
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
      if (element) {
        resizeObserver.unobserve(element);
      }
    };
  }, []);

  const handleTurnOffSettng = (): void => {
    setPositionMenu({
      x: 0,
      y: 0,
      target: {} as PositionOrRoleDto,
    });
    setRoleMenu({
      x: 0,
      y: 0,
      target: {} as PositionOrRoleDto,
    });
  };

  const handleClickOutside = useCallback((e: MouseEvent) => {
    if (inputRef.current && !(inputRef.current! as any).contains(e.target)) {
      handleTurnOffSettng();
      document.removeEventListener("click", handleClickOutside, true);
    }
  }, []);

  useEffect(() => {
    document.addEventListener("click", handleClickOutside, true);
    return () => {
      document.removeEventListener("click", handleClickOutside, true);
    };
  });

  /** insertPositionOrRoleModal: role이나 position을 추가할 수 있는 모달 함수. */
  const insertPositionOrRoleModal = () => {
    return (
      <Modal
        width={490}
        footer={
          <Box justifyContent="flex-end">
            <Button
              label={t("admin.common.button.cancel")}
              type="secondary"
              btnStyle="fill"
              onClick={() => (
                setNewPostionOrRole({ name: "", code: "", order: -1 }),
                toggleInsertPositionOrRole(false)
              )}
            ></Button>
            <Button
              label={t("admin.common.button.ok")}
              type="primary"
              btnStyle="fill"
              state={
                newPositionOrRole?.name?.trim().length > 0
                  ? "default"
                  : "disable"
              }
              onClick={() =>
                insertPositionOrRole(newPositionOrRole.name.trim())
              }
            />
          </Box>
        }
        title={
          tab === 0
            ? t("admin.positionRole.addPosition")
            : t("admin.positionRole.addJob")
        }
        subTitle={
          tab === 0
            ? t("admin.positionRole.addingPosition")
            : t("admin.positionRole.addingJob")
        }
        open={showInsertPositionOrRoleModal}
        onClose={() => toggleInsertPositionOrRole(false)}
        body={
          <Box direction="column" width={"100%"}>
            <Typography type="body2_rg" classes="mg mb-10">
              {tab === 0
                ? t("admin.positionRole.position")
                : t("admin.positionRole.job")}
            </Typography>
            <InputField
              placeholder={
                tab === 0
                  ? t("admin.positionRole.positionInput")
                  : t("admin.positionRole.jobInput")
              }
              maxLength={30}
              size={"md"}
              value={newPositionOrRole.name}
              onChange={(e) => {
                setNewPostionOrRole({
                  name: e.target.value,
                  code: "",
                  order: -1,
                });
              }}
            />
          </Box>
        }
      />
    );
  };

  /** updatePositionOrRoleModal: role이나 position을 수정할 수 있는 모달 함수. */
  const updatePositionOrRoleModal = () => {
    return (
      <Modal
        width={490}
        footer={
          <Box justifyContent="flex-end">
            <Button
              label={t("admin.common.button.cancel")}
              type="secondary"
              btnStyle="fill"
              onClick={() => (
                setNewPostionOrRole({ name: "", code: "", order: -1 }),
                toggleUpdatePositionOrRole(false)
              )}
            />
            <Button
              label={t("admin.common.button.ok")}
              type="primary"
              btnStyle="fill"
              state={
                newPositionOrRole?.name?.trim().length > 0
                  ? "default"
                  : "disable"
              }
              onClick={() => updatePositionOrRole()}
            />
          </Box>
        }
        title={
          tab === 0
            ? t("admin.positionRole.modifyPosition")
            : t("admin.positionRole.modifyJob")
        }
        subTitle={
          tab === 0
            ? t("admin.positionRole.modifyingPosition")
            : t("admin.positionRole.modifyingJob")
        }
        open={showUpdatePositionOrRoleModal}
        onClose={() => toggleUpdatePositionOrRole(false)}
        body={
          <Box classes="position-modalBox" direction="column" width={"100%"}>
            <Typography type="body2_rg" classes="mg mb-10">
              {tab === 0
                ? t("admin.positionRole.position")
                : t("admin.positionRole.job")}
            </Typography>
            <InputField
              placeholder={
                tab === 0
                  ? t("admin.positionRole.positionInput")
                  : t("admin.positionRole.jobInput")
              }
              maxLength={30}
              size={"md"}
              value={newPositionOrRole.name}
              onChange={(e) => {
                setNewPostionOrRole({
                  name: e.target.value,
                  code: newPositionOrRole.code,
                  order: newPositionOrRole.order,
                });
              }}
            />
          </Box>
        }
      />
    );
  };
  return (
    <Box classes="positionRole" direction="column">
      {showInsertPositionOrRoleModal && insertPositionOrRoleModal()}
      {showUpdatePositionOrRoleModal && updatePositionOrRoleModal()}
      {positionMenu.x > 0 &&
        positionMenu.y > 0 &&
        renderPositionMenuButton(positionMenu)}
      {roleMenu.x > 0 && roleMenu.y > 0 && renderRoleMenuButton(roleMenu)}
      <Box classes="positionRole-title" justifyContent="space-between">
        <Typography type="h1">{t("admin.menu.position")}</Typography>
        <Box direction="row" height={40} alignItems="center">
          {tab === 0 ? (
            <Box classes="separateBox">
              {checkboxGroup.selected.length > 0 && (
                <Button
                  label={t("admin.common.button.delete")}
                  type="secondary"
                  btnStyle="fill"
                  onClick={() => deletePositionOrRole()}
                  classes="mg mr-8"
                />
              )}
              <Button
                label={t("admin.common.button.add")}
                type="primary"
                btnStyle="fill"
                onClick={() => toggleInsertPositionOrRole(true)}
              />
            </Box>
          ) : (
            <Box classes="separateBox">
              {checkboxGroup.selected.length > 0 && (
                <Button
                  label={t("admin.common.button.delete")}
                  type="secondary"
                  btnStyle="fill"
                  onClick={() => deletePositionOrRole()}
                  classes="mg mr-8"
                />
              )}
              <Button
                label={t("admin.common.button.add")}
                type="primary"
                btnStyle="fill"
                onClick={() => toggleInsertPositionOrRole(true)}
              />
            </Box>
          )}
        </Box>
      </Box>

      <Box direction="column">
        <Tabs
          tabs={[
            {
              color: "default",
              isNew: false,
              text: (
                <Box>
                  <Typography type="subtitle2">
                    {t("admin.positionRole.position")}
                  </Typography>
                  <Typography
                    classes="mg ml-8"
                    type="subtitle2"
                    exactColor={getColor("COLOR_TEXT_BRAND")}
                  >
                    {positionList.length}
                  </Typography>
                </Box>
              ),
            },
            {
              color: "default",
              isNew: false,
              text: (
                <Box>
                  <Typography type="subtitle2">
                    {t("admin.positionRole.job")}
                  </Typography>
                  <Typography
                    classes="mg ml-8"
                    type="subtitle2"
                    exactColor={getColor("COLOR_TEXT_BRAND")}
                  >
                    {roleList.length}
                  </Typography>
                </Box>
              ),
            },
          ]}
          selectTab={(value) => {
            setTab(value);
          }}
          type={"leftFull"}
          selected={tab}
          size={"md"}
        />
        <Box ref={tableRef} classes="mg mt-12">
          {tab === 0 ? (
            <Table
              width={"100%"}
              height={height + "px"}
              header={[
                {
                  headerText: t("admin.positionRole.order"),
                  width: "200px",
                  order: "",
                  orderAction: () => {},
                },
                {
                  headerText: t("admin.positionRole.position"),
                  width: "calc(100% - 250px)",
                  order: "",
                  orderAction: () => {},
                },
              ]}
              data={transformPositionList(positionList)}
              totalCount={positionList ? positionList.length : 0}
              clickIds={checkboxGroup.selected.map((index) => String(index))}
              checkBoxClick={() =>
                checkboxGroup.handleAllClick(
                  checkboxGroup.selected.length === positionList.length
                    ? []
                    : positionList.map((q) => q.code + "")
                )
              }
              endReached={() => {}}
              columnHeight="md"
            />
          ) : (
            <Table
              width={"100%"}
              height={height + "px"}
              header={[
                {
                  headerText: t("admin.positionRole.order"),
                  width: "200px",
                  order: "",
                  orderAction: () => {},
                },
                {
                  headerText: t("admin.positionRole.job"),
                  width: "calc(100% - 250px)",
                  order: "",
                  orderAction: () => {},
                },
              ]}
              data={transformRoleList(roleList)}
              totalCount={roleList ? roleList.length : 0}
              clickIds={checkboxGroup.selected.map((index) => String(index))}
              checkBoxClick={() =>
                checkboxGroup.handleAllClick(
                  checkboxGroup.selected.length === roleList.length
                    ? []
                    : roleList.map((q) => q.code + "")
                )
              }
              endReached={() => {}}
              columnHeight="md"
            />
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default Position;
